import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

import { getSmartImage } from '../../helpers/helpers';
import { fmtMoney } from '../../helpers/spotlights_dashboard_helpers';
import { getTrials, searchBrands, grantTrial, grantEvergreen } from '../../api/spotlights';
import './TrialGrantModal.scss';

const isGrantedStatus = s => ['created', 'reactivated', 'created_evergreen', 'converted_evergreen'].includes(s);

// Summarize a bulk grant result into a one-line toast.
const summarize = results => {
  const granted = results.filter(r => isGrantedStatus(r.status)).length;
  const active = results.filter(r => r.status === 'skipped_already_active').length;
  const onStripe = results.filter(r => r.status === 'skipped_live_on_stripe').length;
  const noSub = results.filter(r => r.status === 'skipped_no_subscription' || r.status === 'skipped_no_brand').length;
  const parts = [];
  if (granted) parts.push(`${granted} granted`);
  if (active) parts.push(`${active} already active`);
  if (onStripe) parts.push(`${onStripe} skipped (live on Stripe)`);
  if (noSub) parts.push(`${noSub} skipped (no subscription)`);
  return parts.join(' · ') || 'No changes';
};

// Grant Spotlights to a brand. Two modes: a finite paid trial, or evergreen $0 (included in the
// brand's package, not separately billable — converts any existing pre-Stripe trial in place).
// Mirrors the VIP modal: search a single brand (even if not in the table) or paste a comma-separated
// list of Brand IDs. Self-contained — fetches the offer so it works from the Billing/Trials tab, the
// Brands tab button, or a per-brand row action (presetBrand).
const TrialGrantModal = ({ presetBrand, onClose, onDone }) => {
  const [offer, setOffer] = useState(null);
  const [offerError, setOfferError] = useState(null);
  const [loadingOffer, setLoadingOffer] = useState(true);

  const [grantType, setGrantType] = useState('trial'); // 'trial' | 'evergreen'
  const [days, setDays] = useState(30);
  const [mode, setMode] = useState('search'); // 'search' | 'bulk'

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const debounceRef = useRef(null);

  const [bulkIds, setBulkIds] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [presetSaving, setPresetSaving] = useState(false);

  const isEvergreen = grantType === 'evergreen';
  const grantLabel = isEvergreen ? 'Grant $0 access' : 'Grant trial';

  useEffect(() => {
    getTrials()
      .then(r => {
        setOffer(r.offer || null);
        setOfferError(r.offerError || null);
      })
      .catch(e => setOfferError(typeof e === 'string' ? e : 'Could not load offer.'))
      .finally(() => setLoadingOffer(false));
  }, []);

  useEffect(() => {
    if (presetBrand || mode !== 'search') return undefined;
    if (!query.trim()) {
      setResults([]);
      return undefined;
    }
    clearTimeout(debounceRef.current);
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      searchBrands(query.trim())
        .then(r => setResults(r.brands || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, mode, presetBrand]);

  const validDays = Number.isFinite(Number(days)) && Number(days) > 0;

  // One place to translate a per-brand result status into user-facing copy, shared by the single,
  // preset, and search-row grant paths so trial and evergreen wording stay in sync.
  const toastForStatus = (status, brandName) => {
    if (!status || status === 'errored') return cogoToast.error(`Could not grant to ${brandName}`);
    if (status === 'skipped_already_active') return cogoToast.warn(`${brandName} already has an active trial`);
    if (status === 'skipped_live_on_stripe') return cogoToast.error(`${brandName} is live on Stripe — cancel via the UI first`);
    if (status === 'skipped_no_subscription' || status === 'skipped_no_brand') return cogoToast.error(`${brandName} has no Subscription row`);
    if (isEvergreen) return cogoToast.success(`Granted evergreen Spotlights ($0) to ${brandName}`);
    return cogoToast.success(`Granted ${days}-day trial to ${brandName}`);
  };

  const grantToIds = async (ids, { onResult } = {}) => {
    if (isEvergreen) {
      const r = await grantEvergreen({ brandIds: ids, spotlightEnabled: true });
      onResult && onResult(r.results || []);
      return r;
    }
    if (!validDays) {
      cogoToast.error('Enter a valid trial length.');
      return null;
    }
    const r = await grantTrial({ brandIds: ids, days: Number(days), spotlightEnabled: true });
    onResult && onResult(r.results || []);
    return r;
  };

  const grantOne = async brand => {
    setSavingId(brand.Brand_id);
    try {
      const r = await grantToIds([brand.Brand_id]);
      if (!r) return;
      const status = (r.results || []).find(x => x.brandId === brand.Brand_id)?.status;
      toastForStatus(status, brand.brandName);
      setResults(results.map(x => (x.Brand_id === brand.Brand_id ? { ...x, granted: status } : x)));
      onDone && onDone();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to grant.');
    } finally {
      setSavingId(null);
    }
  };

  const grantPreset = async () => {
    setPresetSaving(true);
    try {
      const r = await grantToIds([presetBrand.Brand_id]);
      if (!r) return;
      const status = (r.results || []).find(x => x.brandId === presetBrand.Brand_id)?.status;
      toastForStatus(status, presetBrand.brandName);
      onDone && onDone();
      onClose();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to grant.');
    } finally {
      setPresetSaving(false);
    }
  };

  const submitBulk = async () => {
    const ids = bulkIds
      .split(',')
      .map(s => parseInt(s.trim(), 10))
      .filter(Boolean);
    if (!ids.length) return cogoToast.error('Enter at least one Brand ID.');
    setBulkSaving(true);
    try {
      const r = await grantToIds(ids);
      if (!r) return;
      cogoToast.success(summarize(r.results || []));
      onDone && onDone();
      onClose();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to grant.');
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <div className='sl-modal-overlay' onMouseDown={onClose}>
      <div className='sl-modal' onMouseDown={e => e.stopPropagation()}>
        <div className='sl-modal-head'>
          <div className='sl-modal-title'>Grant Spotlights access</div>
          <button className='sl-modal-close' onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className='sl-modal-body'>
          {loadingOffer ? (
            <div className='sl-empty'>Loading…</div>
          ) : offerError ? (
            <div className='sl-empty' style={{ textAlign: 'left', padding: '4px 0' }}>
              No <strong>SPOTLIGHTS</strong> subscription offer is configured, so access can't be granted yet. Add the SPOTLIGHTS feature to a
              SubscriptionOffer and reload.
            </div>
          ) : (
            <>
              {offer && (
                <div className='sl-card-sub' style={{ marginBottom: 16 }}>
                  Offer: <strong>{offer.title}</strong> (#{offer.id}) — {fmtMoney(offer.retailPrice)} after trial
                </div>
              )}

              <div className='sl-segmented' style={{ marginBottom: 16 }}>
                <div className={cn('sl-segment', { active: !isEvergreen })} onClick={() => setGrantType('trial')}>
                  Trial
                </div>
                <div className={cn('sl-segment', { active: isEvergreen })} onClick={() => setGrantType('evergreen')}>
                  Evergreen · $0
                </div>
              </div>

              {/* Trial length (spotlights are always enabled on grant); evergreen is $0 with no expiry */}
              {isEvergreen ? (
                <div className='sl-card-sub' style={{ marginBottom: 4 }}>
                  Included in the brand's package — access stays on, never billed, no expiry. Converts any existing pre-Stripe trial in place.
                </div>
              ) : (
                <div className='sl-trial-settings'>
                  <label className='sl-trial-field'>
                    <span>Trial length (days)</span>
                    <input type='number' min='1' value={days} onChange={e => setDays(e.target.value)} />
                  </label>
                </div>
              )}

              {presetBrand ? (
                <div className='sl-vip-results' style={{ marginTop: 18 }}>
                  <div className='sl-vip-result'>
                    <div className='sl-vip-result-id'>
                      {getSmartImage(presetBrand.brandLogo) ? <img src={getSmartImage(presetBrand.brandLogo)} alt='' /> : <div className='sl-vip-avatar-placeholder' />}
                      <div>
                        <div className='name'>{presetBrand.brandName}</div>
                        <div className='sub'>Brand {presetBrand.Brand_id}</div>
                      </div>
                    </div>
                    <button className='sl-btn small' disabled={presetSaving} onClick={grantPreset}>
                      {grantLabel}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className='sl-segmented' style={{ margin: '18px 0' }}>
                    <div className={cn('sl-segment', { active: mode === 'search' })} onClick={() => setMode('search')}>
                      Search a brand
                    </div>
                    <div className={cn('sl-segment', { active: mode === 'bulk' })} onClick={() => setMode('bulk')}>
                      Bulk by IDs
                    </div>
                  </div>

                  {mode === 'search' ? (
                    <>
                      <input className='sl-search' type='text' autoFocus placeholder='Search by brand name or ID…' value={query} onChange={e => setQuery(e.target.value)} />
                      <div className='sl-vip-results'>
                        {searching && <div className='sl-empty' style={{ padding: 20 }}>Searching…</div>}
                        {!searching &&
                          results.map(b => (
                            <div className='sl-vip-result' key={b.Brand_id}>
                              <div className='sl-vip-result-id'>
                                {getSmartImage(b.brandLogo) ? <img src={getSmartImage(b.brandLogo)} alt='' /> : <div className='sl-vip-avatar-placeholder' />}
                                <div>
                                  <div className='name'>{b.brandName}</div>
                                  <div className='sub'>
                                    Brand {b.Brand_id}
                                    {b.isSpotlightEnabled ? ' · spotlights on' : ''}
                                  </div>
                                </div>
                              </div>
                              {isGrantedStatus(b.granted) ? (
                                <span className='sl-pill green'>{isEvergreen ? 'Included ($0)' : 'Trial granted'}</span>
                              ) : b.granted === 'skipped_already_active' ? (
                                <span className='sl-pill amber'>Active</span>
                              ) : b.granted === 'skipped_live_on_stripe' ? (
                                <span className='sl-pill amber'>On Stripe</span>
                              ) : (
                                <button className='sl-btn small' disabled={savingId === b.Brand_id} onClick={() => grantOne(b)}>
                                  {grantLabel}
                                </button>
                              )}
                            </div>
                          ))}
                        {!searching && query.trim() && !results.length && <div className='sl-empty' style={{ padding: 20 }}>No matches.</div>}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className='sl-card-label' style={{ marginBottom: 8 }}>Brand IDs (comma-separated)</div>
                      <textarea className='sl-vip-textarea' autoFocus placeholder='e.g. 123, 456, 789' value={bulkIds} onChange={e => setBulkIds(e.target.value)} />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                        <button className='sl-btn' disabled={bulkSaving || !bulkIds.trim()} onClick={submitBulk}>
                          {bulkSaving ? 'Working…' : grantLabel}
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialGrantModal;
